import React, { useState, useEffect } from 'react'
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useClinic } from '@/contexts/ClinicContext'
import { useToast } from '@/hooks/use-toast'
import { billingService } from '@/services/billingService'
import { StripeProvider } from './StripeProvider'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Loader2, CreditCard, CheckCircle2, ChevronDown } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

// Inner component that uses Stripe hooks — uses confirmCardSetup with SetupIntent
const PaymentSetupForm: React.FC<{
  clinicId: string
  clientSecret: string
  isReactivation?: boolean
  onSuccess?: () => void
  onSkip?: () => void
}> = ({ clinicId, clientSecret, isReactivation, onSuccess, onSkip }) => {
  const stripe = useStripe()
  const elements = useElements()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isSkipping, setIsSkipping] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const { isGuided } = useClinic()

  const handleSubmit = async () => {
    if (!stripe || !elements || !termsAccepted || !clientSecret) return

    setIsLoading(true)
    try {
      const { setupIntent, error: confirmError } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      })

      if (confirmError) {
        toast({
          title: 'Card Error',
          description: confirmError.message || 'Failed to verify card',
          variant: 'destructive',
        })
        return
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : (setupIntent?.payment_method as { id?: string })?.id
      if (!paymentMethodId) throw new Error('No payment method returned')

      const result = await billingService.subscribe(clinicId, paymentMethodId)

      if (result.success) {
        toast({
          title: isReactivation ? 'Billing Reactivated' : 'Billing Activated',
          description: isReactivation
            ? 'Your subscription has been reactivated. Billing is now active.'
            : 'Your payment method has been saved and billing is now active.',
        })
        onSuccess?.()
      } else {
        toast({
          title: 'Subscription Failed',
          description: result.error || 'Failed to activate billing',
          variant: 'destructive',
        })
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkip = async () => {
    setIsSkipping(true)
    try {
      const result = await billingService.skipBillingSetup(clinicId)
      if (result.success) {
        toast({ title: 'Billing setup skipped', description: 'You can set up billing later from Settings.' })
        onSkip?.()
      } else {
        toast({
          title: 'Could not skip',
          description: result.error || 'Failed to skip billing setup. Please try again.',
          variant: 'destructive',
        })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setIsSkipping(false)
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-nimbus">
          <CreditCard className="h-7 w-7 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900">
          {isReactivation ? 'Reactivate Billing' : 'Set Up Monthly Billing'}
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          {isReactivation
            ? 'Re-enter your payment method to reactivate monthly billing and resume invoicing.'
            : 'Usage-based billing — you only pay for services rendered.'}
        </p>
      </div>

      {/* Pricing Table — Collapsible */}
      <Collapsible defaultOpen={false}>
        <Card className="border-slate-200">
          <CollapsibleTrigger asChild>
            <button className="w-full text-left">
              <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Service Pricing</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Usage-based pricing — platform fee included. Click to view details.</p>
                  </div>
                  <ChevronDown className="h-5 w-5 text-slate-400 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </div>
              </CardHeader>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-3 pt-0">
              {!isGuided && (
                <>
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900">MDI Visit</p>
                      <p className="text-xs text-slate-500">Medical Director Involvement</p>
                    </div>
                    <Badge variant="secondary" className="bg-brand-teal/10 text-brand-teal">$50/visit</Badge>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900">Consulting Visit</p>
                      <p className="text-xs text-slate-500">Consulting physician visit</p>
                    </div>
                    <Badge variant="secondary" className="bg-brand-teal/10 text-brand-teal">$75/visit</Badge>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900">Lab Order</p>
                      <p className="text-xs text-slate-500">Lab order processing fee</p>
                    </div>
                    <Badge variant="secondary" className="bg-brand-teal/10 text-brand-teal">$150/order</Badge>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900">At-Home Phlebotomy</p>
                      <p className="text-xs text-slate-500">At-home phlebotomy service</p>
                    </div>
                    <Badge variant="secondary" className="bg-brand-teal/10 text-brand-teal">$99/visit</Badge>
                  </div>
                </>
              )}
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                <div>
                  <p className="font-medium text-slate-900">Platform Fee</p>
                  <p className="text-xs text-slate-500">NovaMed platform access</p>
                </div>
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-600">Included</Badge>
              </div>
              <p className="text-center text-xs text-slate-400">
                Invoiced on the 1st of each month for services used in the prior month.
              </p>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Card Input */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Payment Method</CardTitle>
          <CardDescription>Your card will be charged monthly for services used.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-slate-200 p-4">
            <PaymentElement options={{ layout: 'tabs' }} />
          </div>

          {/* Terms Checkbox */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(checked === true)}
              className="mt-0.5"
            />
            <label htmlFor="terms" className="text-sm leading-relaxed text-slate-600">
              I agree to the <a href="https://www.nimbus-os.com/terms" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline hover:text-teal-700">NovaMed Billing Terms</a> and authorize monthly charges to
              the card above for services rendered by my clinic.
            </label>
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !termsAccepted || !stripe}
            className="w-full bg-brand-teal hover:bg-brand-teal/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Activating...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Save Card & Activate Billing
              </>
            )}
          </Button>

          {/* Skip — not shown during reactivation */}
          {!isReactivation && onSkip && (
            <div className="text-center">
              <button
                onClick={handleSkip}
                disabled={isSkipping}
                className="text-sm text-slate-400 underline underline-offset-2 hover:text-slate-600"
              >
                {isSkipping ? 'Skipping...' : 'Skip for now — set up within 7 days'}
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// Outer component that wraps with StripeProvider
interface PaymentSetupProps {
  isReactivation?: boolean
  onSuccess?: () => void
  onSkip?: () => void
}

export const PaymentSetup: React.FC<PaymentSetupProps> = ({ isReactivation, onSuccess, onSkip }) => {
  const { clinic } = useClinic()
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!clinic?.id) return

    const init = async () => {
      try {
        const result = await billingService.createSetupIntent(clinic.id)
        if (result?.clientSecret) {
          setClientSecret(result.clientSecret)
        }
      } catch (err) {
        console.error('[PaymentSetup] Failed to create setup intent:', err)
      } finally {
        setIsLoading(false)
      }
    }
    init()
  }, [clinic?.id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
      </div>
    )
  }

  if (!clientSecret) {
    return (
      <div className="mx-auto max-w-lg space-y-4 text-center">
        <p className="text-sm text-slate-500">Unable to initialize billing setup. Please try again.</p>
        {onSkip && (
          <button onClick={onSkip} className="text-sm text-slate-400 underline hover:text-slate-600">
            Skip for now
          </button>
        )}
      </div>
    )
  }

  return (
    <StripeProvider clientSecret={clientSecret}>
      <PaymentSetupForm
        clinicId={clinic?.id || ''}
        clientSecret={clientSecret || ''}
        isReactivation={isReactivation}
        onSuccess={onSuccess}
        onSkip={onSkip}
      />
    </StripeProvider>
  )
}

export default PaymentSetup
